import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import TablePagination from '@mui/material/TablePagination';
import Spacings from '~/modules/ui-kit/spacings';
import ContentView from '~/components/ContentView';
import DrawerButton from '~/containers/DrawerButton';
import { NewsEntry, useNewsEntries } from '~/hooks/api/useNewsEntries';
import messages from './messages';
import styles from './styles.module.css';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

type NewsEntryWithStats = NewsEntry & {
  totalReaders?: number | null;
  totalRecipients?: number | null;
};

const route = getRouteApi('/news-entries/');

export const NewsEntryList = () => {
  const navigate = useNavigate();
  const search = route.useSearch();
  const currentPage = search.page ?? 0;
  const perPage = search.perPage ?? 20;

  const { data, isLoading } = useNewsEntries({
    query: {
      page: currentPage,
      perPage,
      sort: ['-validFrom'],
    },
  });

  const newsEntries = (data?.newsEntries as NewsEntryWithStats[]) || [];
  const totalCount = data?.totalCount || 0;

  const createNavigateToNewsEntry = (newsEntryId: number) => () => {
    navigate({
      to: '/news-entries/$newsEntryId',
      params: { newsEntryId: String(newsEntryId) },
      search: {
        from: `/news-entries${search.page !== undefined || search.perPage !== undefined ? `?page=${currentPage}&perPage=${perPage}` : ''}`,
      },
    });
  };

  const onChangePage = (_event: unknown, page: number) => {
    navigate({
      to: '/news-entries',
      search: {
        page,
        perPage,
      },
    });
  };

  const onChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newPerPage = parseInt(event.target.value, 10);
    navigate({
      to: '/news-entries',
      search: {
        page: 0,
        perPage: newPerPage,
      },
    });
  };

  const goToCreateNewsEntry = () => {
    navigate({ to: '/news-entries/create' });
  };

  return (
    <ContentView
      header={
        <div>
          <AppBar position="static">
            <Toolbar>
              <DrawerButton />
              <Typography
                variant="h6"
                sx={{
                  color: 'inherit',
                }}
              >
                <FormattedMessage {...messages.header} />
              </Typography>
            </Toolbar>
          </AppBar>
        </div>
      }
      isLoading={isLoading}
      floatingActionButton={
        <Fab onClick={goToCreateNewsEntry} color="secondary">
          <AddIcon data-testid="create-news-entry" />
        </Fab>
      }
    >
      {!isLoading && newsEntries.length === 0 ? (
        <ListItem data-test="empty-list-item">
          <ListItemText
            primary={
              <Typography
                sx={{
                  color: 'text.secondary',
                }}
              >
                <FormattedMessage {...messages.noNewsEntriesYet} />
              </Typography>
            }
          />
        </ListItem>
      ) : (
        <>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            rowsPerPage={perPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            className={styles['pagination-controls']}
          />
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage {...messages.tableHeaderNews} />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage {...messages.tableHeaderReadBy} />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage {...messages.tableHeaderVisibility} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {newsEntries.map((newsEntry) => (
                  <TableRow
                    hover
                    key={newsEntry.id}
                    onClick={createNavigateToNewsEntry(newsEntry.id!)}
                  >
                    <TableCell>
                      <ListItemText primary={newsEntry.title} />
                    </TableCell>
                    <TableCell>
                      {`${newsEntry.totalReaders ?? 0} / ${newsEntry.totalRecipients ?? 0}`}
                    </TableCell>
                    <TableCell>
                      <Spacings.Stack scale="s">
                        <div>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            <FormattedMessage
                              {...messages.visibleFrom}
                              values={{
                                from: moment(newsEntry.validFrom).format('L'),
                              }}
                            />
                          </Typography>
                        </div>
                        {newsEntry.validUntil ? (
                          <div>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                              }}
                            >
                              <FormattedMessage
                                {...messages.visibleUntil}
                                values={{
                                  until: moment(newsEntry.validUntil).format(
                                    'L'
                                  ),
                                }}
                              />
                            </Typography>
                          </div>
                        ) : null}
                      </Spacings.Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </ContentView>
  );
};

NewsEntryList.displayName = 'NewsEntryList';

export default NewsEntryList;
